
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { FlightBoard } from "@/components/FlightBoard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Loader2, ArrowLeft } from "lucide-react";
import { Flight } from "@/types/flight";
import { getFlights } from "@/services";

export default function AirportPage() {
  const { airportCode } = useParams<{ airportCode: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  
  const { data: flights = [], isLoading } = useQuery({
    queryKey: ['flights'],
    queryFn: () => getFlights(),
  });
  
  // Filter flights for this airport (either departing from or arriving to)
  const airportFlights = flights.filter(flight => 
    flight.departure_city?.code === airportCode || 
    flight.arrival_city?.code === airportCode
  );

  // Get airport name from the first flight
  const airportName = airportFlights.length > 0 
    ? airportFlights.find(f => f.departure_city?.code === airportCode)?.departure_city?.name ||
      airportFlights.find(f => f.arrival_city?.code === airportCode)?.arrival_city?.name
    : airportCode;

  // Filter by search term
  const filteredFlights = airportFlights.filter(flight => {
    const searchLower = searchTerm.toLowerCase();
    return (
      flight.flight_number.toLowerCase().includes(searchLower) ||
      flight.departure_city?.name.toLowerCase().includes(searchLower) ||
      flight.arrival_city?.name.toLowerCase().includes(searchLower) ||
      flight.organization?.name.toLowerCase().includes(searchLower)
    );
  });

  // Separate departures and arrivals
  const departures = filteredFlights.filter(flight => 
    flight.departure_city?.code === airportCode
  );
  
  const arrivals = filteredFlights.filter(flight => 
    flight.arrival_city?.code === airportCode
  );

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Filter for today's flights
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

  const todaysDepartures = departures.filter(flight => {
    const departureDate = new Date(flight.scheduled_departure);
    return departureDate >= startOfToday && departureDate <= endOfToday;
  });

  const todaysArrivals = arrivals.filter(flight => {
    const arrivalDate = new Date(flight.scheduled_arrival);
    return arrivalDate >= startOfToday && arrivalDate <= endOfToday;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 xl:px-48">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to All Flights
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">{airportName} ({airportCode})</h1>
              <p className="text-muted-foreground">{currentDate}</p>
            </div>
          </div>
          
          <div className="flex w-full max-w-sm items-center space-x-2">
            <Input
              placeholder="Search flights, cities or airlines..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" size="icon" variant="ghost">
              <Search className="h-4 w-4" />
              <span className="sr-only">Search</span>
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-lg">Loading flights...</span>
            </div>
          ) : (
            <Tabs defaultValue="departures" className="w-full">
              <div className="border-b">
                <TabsList className="grid w-full grid-cols-2 h-12 bg-muted/50">
                  <TabsTrigger value="departures" className="text-base">
                    Departures ({todaysDepartures.length})
                  </TabsTrigger>
                  <TabsTrigger value="arrivals" className="text-base">
                    Arrivals ({todaysArrivals.length})
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="departures" className="mt-0">
                <FlightStatusSummary flights={todaysDepartures} type="Departures" />
                <FlightBoard flights={todaysDepartures} />
              </TabsContent>
              
              <TabsContent value="arrivals" className="mt-0">
                <FlightStatusSummary flights={todaysArrivals} type="Arrivals" />
                <FlightBoard flights={todaysArrivals} />
              </TabsContent>
            </Tabs>
          )}
        </div>
        
        <div className="text-center text-sm text-muted-foreground mt-6">
          <p>Information is updated in real-time. Last update: {new Date().toLocaleTimeString()}</p>
        </div>
      </div>
    </div>
  );
}

function FlightStatusSummary({ flights, type }: { flights: Flight[]; type: string }) {
  const onTime = flights.filter(f => f.status === "On Time").length;
  const delayed = flights.filter(f => f.status === "Delayed").length;
  const cancelled = flights.filter(f => f.status === "Cancelled").length;
  
  return (
    <div className="flex flex-wrap justify-around border-b py-3 text-center">
      <div>
        <span className="text-sm text-muted-foreground mr-2">Total {type}:</span>
        <span className="font-medium">{flights.length} flights</span>
      </div>
      <div>
        <span className="text-sm text-muted-foreground mr-2">On Time:</span>
        <span className="font-medium text-green-600">{onTime}</span>
      </div>
      <div>
        <span className="text-sm text-muted-foreground mr-2">Delayed:</span>
        <span className="font-medium text-amber-600">{delayed}</span>
      </div>
      <div>
        <span className="text-sm text-muted-foreground mr-2">Cancelled:</span>
        <span className="font-medium text-red-600">{cancelled}</span>
      </div>
    </div>
  );
}
